import { apiCall, isBackendConfigured } from './api-call'
import {
  DiscoverBuilding,
  DiscoverFilters,
  DiscoverListing,
  DiscoverSearchResult,
} from '../_types/quickpass-discover'

const EMPTY_RESULT: DiscoverSearchResult = {
  buildings: [],
  nextCursor: null,
  totalCount: 0,
  enabledPmCount: 0,
}

interface SearchDiscoverInput {
  filters: DiscoverFilters
  cursor: number
  pageSize: number
  quickPassUserId: string | null
}

/**
 * Hits the backend discover search. Returns an empty result when backend
 * is unconfigured — discover is a backend-only product, no localStorage
 * fallback (no real listings to fall back to).
 *
 * @param {SearchDiscoverInput} input
 * @returns {Promise<DiscoverSearchResult>}
 */
const searchDiscoverListings = async (
  input: SearchDiscoverInput,
): Promise<DiscoverSearchResult> => {
  if (isBackendConfigured() === false) {
    return EMPTY_RESULT
  }

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'discoversearch',
      data: {
        filters: input.filters,
        cursor: input.cursor,
        pageSize: input.pageSize,
        quickPassUserId: input.quickPassUserId,
      },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as DiscoverSearchResult
  } catch (error) {
    console.warn('[discover] search failed —', error)
    return EMPTY_RESULT
  }
}

interface GetDiscoverListingInput {
  inventoryId: string
  quickPassUserId: string | null
}

interface GetDiscoverBuildingInput {
  buildingKey: string
  quickPassUserId: string | null
  filters: DiscoverFilters | null
}

/**
 * Loads a single building aggregate for the detail page. When `filters`
 * is provided, the response splits units into `matchingUnits[]` (passed
 * the filter) and `otherUnits[]` (in the same building, didn't pass).
 * When `filters` is null, all units land in `matchingUnits[]`.
 *
 * @param {GetDiscoverBuildingInput} input
 * @returns {Promise<DiscoverBuilding | null>}
 */
const getDiscoverBuilding = async (
  input: GetDiscoverBuildingInput,
): Promise<DiscoverBuilding | null> => {
  if (isBackendConfigured() === false) {
    return null
  }

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'discovergetbuilding',
      data: {
        buildingKey: input.buildingKey,
        quickPassUserId: input.quickPassUserId,
        filters: input.filters,
      },
    })

    if (response.code === 404) return null
    if (response.code !== 200) throw new Error(response.message)
    return JSON.parse(response.data) as DiscoverBuilding
  } catch (error) {
    console.warn('[discover] getDiscoverBuilding failed —', error)
    return null
  }
}

/**
 * Loads a single discover listing (raw inventory row) for the detail page.
 * Kept around for legacy callers — new code should prefer
 * `getDiscoverBuilding` which returns the building aggregate with its
 * units list.
 *
 * @param {GetDiscoverListingInput} input
 * @returns {Promise<DiscoverListing | null>}
 */
const getDiscoverListing = async (
  input: GetDiscoverListingInput,
): Promise<DiscoverListing | null> => {
  if (isBackendConfigured() === false) {
    return null
  }

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'discovergetlisting',
      data: {
        inventoryId: input.inventoryId,
        quickPassUserId: input.quickPassUserId,
      },
    })

    if (response.code === 404) {
      return null
    }

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as DiscoverListing
  } catch (error) {
    console.warn('[discover] getDiscoverListing failed —', error)
    return null
  }
}

interface SubmitInquiryInput {
  quickPassUserId: string
  inventoryId: string
  message: string
  customFields: Record<string, string>
}

interface SubmitInquiryResult {
  inquiryId: string
  status: 'created' | 'already-sent'
}

/**
 * Sends a renter inquiry on a discover listing. The partner picks it up
 * server-side as a `quickpass-inquiries` doc the admin-portal renders.
 *
 * @param {SubmitInquiryInput} input
 * @returns {Promise<SubmitInquiryResult | null>}
 */
const submitDiscoverInquiry = async (
  input: SubmitInquiryInput,
): Promise<SubmitInquiryResult | null> => {
  if (isBackendConfigured() === false) {
    return null
  }

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'submitinquiry',
      data: {
        quickPassUserId: input.quickPassUserId,
        inventoryId: input.inventoryId,
        message: input.message,
        customFields: input.customFields,
      },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as SubmitInquiryResult
  } catch (error) {
    console.warn('[discover] submitInquiry failed —', error)
    return null
  }
}

interface TrackDiscoverViewInput {
  quickPassUserId: string
  inventoryId: string
}

const trackDiscoverView = async (
  input: TrackDiscoverViewInput,
): Promise<void> => {
  if (isBackendConfigured() === false) return

  try {
    await apiCall({
      group: 'quickpass',
      command: 'trackdiscoverview',
      data: {
        quickPassUserId: input.quickPassUserId,
        inventoryId: input.inventoryId,
      },
    })
  } catch (error) {
    console.warn('[discover] trackDiscoverView failed —', error)
  }
}

interface GetSimilarInput {
  inventoryId: string
  quickPassUserId: string | null
  limit?: number
}

const getSimilarDiscoverListings = async (
  input: GetSimilarInput,
): Promise<DiscoverListing[]> => {
  if (isBackendConfigured() === false) return []

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'discoversimilar',
      data: {
        inventoryId: input.inventoryId,
        quickPassUserId: input.quickPassUserId,
        limit: input.limit ?? 6,
      },
    })

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    const parsed = JSON.parse(response.data) as { listings: DiscoverListing[] }
    return parsed.listings
  } catch (error) {
    console.warn('[discover] getSimilar failed —', error)
    return []
  }
}

interface FetchByIdsInput {
  ids: string[]
  quickPassUserId: string | null
}

/**
 * Hydrates a set of inventory IDs into DiscoverListing payloads. Used by
 * the dashboard recently-viewed rail. Per-id Promise.all — for now the
 * recently-viewed cap is small enough (24) that batching isn't worth a
 * dedicated server endpoint.
 *
 * @param {FetchByIdsInput} input
 * @returns {Promise<DiscoverListing[]>}
 */
const fetchDiscoverListingsByIds = async (
  input: FetchByIdsInput,
): Promise<DiscoverListing[]> => {
  if (isBackendConfigured() === false || input.ids.length === 0) return []

  const results = await Promise.all(
    input.ids.map(id =>
      getDiscoverListing({
        inventoryId: id,
        quickPassUserId: input.quickPassUserId,
      }),
    ),
  )

  return results.filter(
    (listing): listing is DiscoverListing => listing !== null,
  )
}

export {
  searchDiscoverListings,
  getDiscoverListing,
  getDiscoverBuilding,
  submitDiscoverInquiry,
  trackDiscoverView,
  getSimilarDiscoverListings,
  fetchDiscoverListingsByIds,
}
